import React from 'react';
import {
  Loader2,
  Brain,
  Search,
  Database,
  Target,
  Eye,
  CheckCircle,
  Clock,
  Zap
} from 'lucide-react';

/**
 * LoadingSpinner - Basic loading spinner with customizable size and color
 */
export const LoadingSpinner = ({ 
  size = 'md', 
  color = 'blue', 
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12'
  };

  const colorClasses = {
    blue: 'text-blue-600',
    gray: 'text-gray-600',
    green: 'text-green-600',
    orange: 'text-orange-600',
    red: 'text-red-600'
  };

  return (
    <Loader2 
      className={`animate-spin ${sizeClasses[size]} ${colorClasses[color]} ${className}`} 
    />
  );
};

/**
 * ProgressBar - Animated progress bar with percentage display
 */
export const ProgressBar = ({ 
  progress = 0, 
  showPercentage = true, 
  color = 'blue',
  size = 'md',
  animated = true,
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4'
  };

  const colorClasses = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    orange: 'bg-orange-600',
    red: 'bg-red-600'
  };

  return (
    <div className={`w-full ${className}`}>
      <div className={`bg-gray-200 rounded-full ${sizeClasses[size]} overflow-hidden`}>
        <div
          className={`${colorClasses[color]} ${sizeClasses[size]} rounded-full transition-all duration-500 ease-out ${
            animated ? 'animate-pulse' : ''
          }`}
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
      {showPercentage && (
        <div className="text-sm text-gray-600 mt-1 text-center">
          {Math.round(progress)}%
        </div>
      )}
    </div>
  );
};

/**
 * ScenarioGenerationLoader - Specialized loader for scenario generation
 */
export const ScenarioGenerationLoader = ({ 
  stage = 'preparing',
  progress = 0,
  message = '',
  className = ''
}) => {
  const stages = {
    preparing: {
      icon: <Search className="h-6 w-6" />,
      title: 'Préparation',
      description: 'Analyse des données CTI...',
      color: 'blue'
    },
    generating: {
      icon: <Brain className="h-6 w-6" />,
      title: 'Génération IA',
      description: 'Création du scénario opérationnel...',
      color: 'orange'
    },
    processing: {
      icon: <Database className="h-6 w-6" />,
      title: 'Traitement',
      description: 'Structuration des données...',
      color: 'green'
    },
    finalizing: {
      icon: <CheckCircle className="h-6 w-6" />,
      title: 'Finalisation',
      description: 'Préparation de l\'affichage...',
      color: 'green'
    }
  };

  const currentStage = stages[stage] || stages.preparing;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center space-x-4 mb-4">
        <div className={`p-3 rounded-full bg-${currentStage.color}-100 text-${currentStage.color}-600 animate-pulse`}>
          {currentStage.icon}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {currentStage.title}
          </h3>
          <p className="text-sm text-gray-600">
            {message || currentStage.description}
          </p>
        </div>
      </div>
      
      <ProgressBar 
        progress={progress} 
        color={currentStage.color}
        animated={true}
        className="mb-2"
      />
      
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Génération en cours...</span>
        <span>{Math.round(progress)}%</span>
      </div>
    </div>
  );
};

/**
 * PhaseVisualizationLoader - Specialized loader for phase visualization
 */
export const PhaseVisualizationLoader = ({ 
  phase = 'CONNAITRE',
  progress = 0,
  className = ''
}) => {
  const phaseIcons = {
    CONNAITRE: <Search className="h-5 w-5" />,
    RENTRER: <Target className="h-5 w-5" />,
    TROUVER: <Eye className="h-5 w-5" />,
    EXPLOITER: <Database className="h-5 w-5" />
  };

  const phaseColors = {
    CONNAITRE: 'blue',
    RENTRER: 'orange',
    TROUVER: 'purple',
    EXPLOITER: 'red'
  };

  const color = phaseColors[phase] || 'blue';

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      <div className="flex items-center space-x-3 mb-3">
        <div className={`p-2 rounded-lg bg-${color}-100 text-${color}-600`}>
          {phaseIcons[phase] || phaseIcons.CONNAITRE}
        </div>
        <div>
          <h4 className="font-medium text-gray-900">
            Visualisation Phase {phase}
          </h4>
          <p className="text-sm text-gray-600">
            Génération du diagramme...
          </p>
        </div>
      </div>
      
      <ProgressBar 
        progress={progress} 
        color={color}
        size="sm"
        animated={true}
      />
    </div>
  );
};

/**
 * SkeletonCard - Skeleton loading placeholder for cards
 */
export const SkeletonCard = ({ className = '' }) => (
  <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
    <div className="animate-pulse">
      <div className="flex items-center space-x-3 mb-3">
        <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 rounded"></div>
        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        <div className="h-3 bg-gray-200 rounded w-4/6"></div>
      </div>
    </div>
  </div>
);

/**
 * SkeletonTable - Skeleton loading placeholder for tables
 */
export const SkeletonTable = ({ rows = 3, columns = 4, className = '' }) => (
  <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}>
    <div className="animate-pulse">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }).map((_, i) => (
            <div key={i} className="h-4 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
      
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="px-4 py-3 border-b border-gray-200 last:border-b-0">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div key={colIndex} className="h-3 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * LoadingOverlay - Full-screen loading overlay
 */
export const LoadingOverlay = ({ 
  message = 'Chargement...',
  progress = null,
  onCancel = null,
  className = ''
}) => (
  <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${className}`}>
    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
      <div className="text-center">
        <LoadingSpinner size="xl" className="mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {message}
        </h3>
        
        {progress !== null && (
          <ProgressBar 
            progress={progress} 
            className="mb-4"
            animated={true}
          />
        )}
        
        {onCancel && (
          <button
            onClick={onCancel}
            className="mt-4 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Annuler
          </button>
        )}
      </div>
    </div>
  </div>
);

/**
 * InlineLoader - Small inline loading indicator
 */
export const InlineLoader = ({ 
  message = 'Chargement...',
  size = 'sm',
  className = ''
}) => (
  <div className={`flex items-center space-x-2 ${className}`}>
    <LoadingSpinner size={size} />
    <span className="text-sm text-gray-600">{message}</span>
  </div>
);

/**
 * StepProgress - Multi-step progress indicator
 */
export const StepProgress = ({ 
  steps = [],
  currentStep = 0,
  className = ''
}) => (
  <div className={`flex items-center space-x-2 ${className}`}>
    {steps.map((step, index) => (
      <React.Fragment key={index}>
        <div className="flex items-center space-x-2">
          <div className={`
            w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
            ${index < currentStep 
              ? 'bg-green-600 text-white' 
              : index === currentStep 
                ? 'bg-blue-600 text-white animate-pulse' 
                : 'bg-gray-200 text-gray-600'
            }
          `}>
            {index < currentStep ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              index + 1
            )}
          </div>
          <span className={`text-sm ${
            index <= currentStep ? 'text-gray-900 font-medium' : 'text-gray-500'
          }`}>
            {step}
          </span>
        </div>
        
        {index < steps.length - 1 && (
          <div className={`flex-1 h-1 rounded-full ${
            index < currentStep ? 'bg-green-600' : 'bg-gray-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

/**
 * PulsingDot - Simple pulsing indicator
 */
export const PulsingDot = ({ 
  color = 'blue',
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  const colorClasses = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    orange: 'bg-orange-600',
    red: 'bg-red-600'
  };

  return (
    <div className={`
      ${sizeClasses[size]} 
      ${colorClasses[color]} 
      rounded-full animate-pulse
      ${className}
    `} />
  );
};

export default {
  LoadingSpinner,
  ProgressBar,
  ScenarioGenerationLoader,
  PhaseVisualizationLoader,
  SkeletonCard,
  SkeletonTable,
  LoadingOverlay,
  InlineLoader,
  StepProgress,
  PulsingDot
};
